/**
 * NOTE: This code was created with the assistance of AI, particularly for all the
 * mathematical calculations and rendering logic related to parallax effects.
 */

import { useLayoutEffect, useRef } from 'react';

import { useIsMobile } from 'src/app/shared/hooks/userAgent';
import { isMotionReduced } from 'src/app/shared/utils/accessibility';

import type { ParallaxState, RectState, UseParallaxWrapperReturn } from '../../types';

export type UseParallaxWrapperProps = {
	strength: number;
	maxShift: number;
	mobileStrength: number;
	mobileMaxShift: number;
};

const createReadRect =
	(el: HTMLDivElement) =>
	(state: RectState): RectState => {
		const r = el.getBoundingClientRect();
		const scrollY = Math.max(0, window.scrollY || 0); // Prevent negative scrollY on iOS bounce
		const rectTop = r.top + scrollY;
		// Fix initial position calculation - use null check instead of 0 check
		const initialElementTop = state.initialElementTop === null ? rectTop : state.initialElementTop;
		return { rectTop, initialElementTop };
	};

const createOnScroll =
	(effectiveStrength: number, effectiveMaxShift: number, isMobile: boolean) =>
	(state: RectState): number => {
		// Clamp scrollY to prevent iOS bounce scrolling issues
		const scrollY = Math.max(
			0,
			Math.min(window.scrollY || 0, document.documentElement.scrollHeight - window.innerHeight),
		);
		const currentElementTop = state.rectTop - scrollY;
		const displacement = currentElementTop - (state.initialElementTop || 0);

		// Use stable viewport height that doesn't change with iOS address bar
		const viewportHeight = isMobile
			? window.screen.height || document.documentElement.clientHeight || 800
			: window.innerHeight || document.documentElement.clientHeight || 800;
		const progress = -displacement / viewportHeight;
		return Math.max(Math.min(progress * 100 * effectiveStrength, effectiveMaxShift), -effectiveMaxShift);
	};

const updateRectState = (readRect: (state: RectState) => RectState, currentState: ParallaxState): ParallaxState => {
	const newRectState = readRect(currentState);
	return { ...currentState, ...newRectState };
};

const createScrollLogic = (options: {
	el: HTMLDivElement;
	isMobile: boolean;
	calculateShift: (state: RectState) => number;
	updateRect: () => void;
	state: { current: ParallaxState };
	hasUserScrolled: { current: boolean };
	scrollTimeout: { current: ReturnType<typeof setTimeout> | null };
}) => {
	const { el, isMobile, calculateShift, updateRect, state, hasUserScrolled, scrollTimeout } = options;
	const performUpdate = (isUserScroll: boolean) => {
		updateRect();
		const shift = calculateShift(state.current);
		el.style.transform = `translateY(${shift}px)`;

		if (isMobile && isUserScroll) {
			scrollTimeout.current = setTimeout(() => {
				if (!state.current.running) {
					updateRect();
					el.style.transform = `translateY(${calculateShift(state.current)}px)`;
				}
			}, 50);
		}
	};

	const onScroll = (isUserScroll = true) => {
		if (state.current.running) return;
		state.current.running = true;

		if (isUserScroll && !hasUserScrolled.current) {
			hasUserScrolled.current = true;
			el.dataset.scrolling = '';
		}

		if (scrollTimeout.current) {
			clearTimeout(scrollTimeout.current);
			scrollTimeout.current = null;
		}

		state.current.rafId = window.requestAnimationFrame(() => {
			state.current.running = false;
			performUpdate(isUserScroll);
		});
	};

	return onScroll;
};

export function useParallaxWrapper({
	strength,
	maxShift,
	mobileStrength,
	mobileMaxShift,
}: UseParallaxWrapperProps): UseParallaxWrapperReturn {
	const isMobile = useIsMobile();
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	useLayoutEffect(() => {
		const supportsRAF = typeof window !== 'undefined' && 'requestAnimationFrame' in window;
		if (isMotionReduced() || !supportsRAF || !wrapperRef.current) return undefined;

		const el = wrapperRef.current;
		const effectiveStrength = isMobile ? mobileStrength : strength;
		const effectiveMaxShift = isMobile ? mobileMaxShift : maxShift;

		const stateRef: { current: ParallaxState } = {
			current: { rafId: 0, running: false, rectTop: 0, initialElementTop: null },
		};
		const hasUserScrolledRef = { current: false };
		const scrollTimeoutRef: { current: ReturnType<typeof setTimeout> | null } = { current: null };

		const readRect = createReadRect(el);
		const calculateShift = createOnScroll(effectiveStrength, effectiveMaxShift, isMobile);
		const updateRect = () => {
			stateRef.current = updateRectState(readRect, stateRef.current);
		};

		const onScroll = createScrollLogic({
			el,
			isMobile,
			calculateShift,
			updateRect,
			state: stateRef,
			hasUserScrolled: hasUserScrolledRef,
			scrollTimeout: scrollTimeoutRef,
		});
		const scrollHandler = () => onScroll();

		updateRect();
		window.addEventListener('scroll', scrollHandler, { passive: true });
		window.addEventListener('resize', updateRect);
		onScroll(false);

		return () => {
			window.removeEventListener('scroll', scrollHandler);
			window.removeEventListener('resize', updateRect);
			window.cancelAnimationFrame(stateRef.current.rafId);
			if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
			el.style.transform = '';
			delete el.dataset.scrolling;
		};
	}, [isMobile, strength, maxShift, mobileStrength, mobileMaxShift]);

	return { wrapperRef };
}
